import React, { ReactNode, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { X, Menu } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

interface PublicLayoutProps {
  children: ReactNode;
}

export const PublicLayout: React.FC<PublicLayoutProps> = ({ children }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { company } = useAuth();

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Helper to handle navigation (for buttons instead of asChild)
  const handleNavigation = (path: string) => {
    navigate(path);
    setIsMobileMenuOpen(false); // Close mobile menu after navigation
  };

  // Dynamic background based on route (professional gradients with patterns)
  const getBackgroundClass = () => {
    const path = location.pathname;
    
    // Base gradient for all pages
    let gradientClass = 'bg-gradient-to-br from-background via-white to-muted/20';
    let patternClass = 'bg-pattern-light'; // Default subtle pattern
    
    // Route-specific enhancements
    if (path === '/' || path === '/home') {
      // Home: Energetic blue gradient with subtle energy pattern
      gradientClass = 'bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50';
      patternClass = 'bg-pattern-dots'; // Dot pattern for energy
    } else if (path === '/pricing') {
      // Pricing: Clean green tones for money/growth theme with geometric pattern
      gradientClass = 'bg-gradient-to-br from-emerald-50 via-green-50 to-teal-50';
      patternClass = 'bg-pattern-geo'; // Geometric pattern for structure
    } else if (path === '/about') {
      // About: Warm, welcoming orange gradient with wave pattern
      gradientClass = 'bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50';
      patternClass = 'bg-pattern-waves'; // Wave pattern for flow
    } else if (path === '/contact') {
      // Contact: Professional blue for communication with subtle lines
      gradientClass = 'bg-gradient-to-br from-cyan-50 via-blue-50 to-indigo-50';
      patternClass = 'bg-pattern-lines'; // Line pattern for connection
    } else if (path === '/terms' || path === '/privacy') {
      // Terms/Privacy: Neutral gray for readability with minimal dots
      gradientClass = 'bg-gradient-to-br from-gray-50 via-slate-50 to-zinc-50';
      patternClass = 'bg-pattern-minimal'; // Minimal pattern for focus
    } else if (path === '/login' || path === '/signup' || path === '/forgot-password' || path === '/reset-password') {
      // Auth pages: Clean white with subtle blue accent and light dots
      gradientClass = 'bg-gradient-to-br from-white via-blue-50 to-indigo-50';
      patternClass = 'bg-pattern-dots-light'; // Light dots for trust
    }

    return `${gradientClass} ${patternClass}`;
  };

  return (
    <div className={`min-h-screen flex flex-col ${getBackgroundClass()}`}>
      {/* Fixed Warning Banner */}
      <div className="fixed top-0 left-0 right-0 bg-red-600 text-white text-center p-2 text-sm font-medium z-50">
        Warning: This is a development environment. Data may be reset without notice. Please do not sign up.
      </div>

      {/* Header: Logo left, Nav center (hidden on mobile), Auth buttons right (stack on mobile) */}
      <header className="bg-background/95 backdrop-blur-sm border-b border-border/50 shadow-sm">
        <div className="container mx-auto px-4 py-2">
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0 mr-4 md:mr-8">
              <img 
                src={company?.logo_url || "/WWConnectlogo.png"} 
                alt={company?.name ? `${company.name} Logo` : "WightWay Connect Logo"} 
                className="h-32 md:h-40 w-auto" // Responsive: smaller on mobile
              />
            </Link>
            {/* Desktop Nav: Hidden on mobile */}
            <nav className="hidden md:flex flex-1 justify-center space-x-6">
              <button onClick={() => handleNavigation('/')} className="text-muted-foreground hover:text-foreground transition-colors">Home</button>
              <button onClick={() => handleNavigation('/pricing')} className="text-muted-foreground hover:text-foreground transition-colors">Pricing</button>
              <button onClick={() => handleNavigation('/about')} className="text-muted-foreground hover:text-foreground transition-colors">About</button>
              <button onClick={() => handleNavigation('/contact')} className="text-muted-foreground hover:text-foreground transition-colors">Contact</button>
            </nav>
            {/* Desktop Auth Buttons */}
            <div className="hidden sm:flex items-center space-x-2 ml-8">
              <button onClick={() => handleNavigation('/login')} className="mr-2">
                <Button variant="ghost">Login</Button>
              </button>
              <button onClick={() => handleNavigation('/signup')}>
                <Button>Sign Up</Button>
              </button>
            </div>
            {/* Mobile Menu Button */}
            <div className="sm:hidden ml-auto">
              <Button variant="ghost" size="icon" onClick={toggleMobileMenu}>
                {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </Button>
            </div>
          </div>

          {/* Mobile Menu: Full overlay on small screens */}
          {isMobileMenuOpen && (
            <div className="sm:hidden fixed inset-0 bg-background/95 backdrop-blur-sm z-40 border-t border-border shadow-lg">
              <div className="flex flex-col h-full">
                {/* Mobile Nav Links */}
                <div className="flex-1 p-4 space-y-4">
                  <button 
                    onClick={() => { handleNavigation('/'); toggleMobileMenu(); }}
                    className="block py-2 px-4 text-muted-foreground hover:text-foreground transition-colors border-b border-border"
                  >
                    Home
                  </button>
                  <button 
                    onClick={() => { handleNavigation('/pricing'); toggleMobileMenu(); }}
                    className="block py-2 px-4 text-muted-foreground hover:text-foreground transition-colors border-b border-border"
                  >
                    Pricing
                  </button>
                  <button 
                    onClick={() => { handleNavigation('/about'); toggleMobileMenu(); }}
                    className="block py-2 px-4 text-muted-foreground hover:text-foreground transition-colors border-b border-border"
                  >
                    About
                  </button>
                  <button 
                    onClick={() => { handleNavigation('/contact'); toggleMobileMenu(); }}
                    className="block py-2 px-4 text-muted-foreground hover:text-foreground transition-colors border-b border-border"
                  >
                    Contact
                  </button>
                  {/* Mobile Auth Buttons */}
                  <div className="pt-4 space-y-2">
                    <button 
                      onClick={() => { handleNavigation('/login'); toggleMobileMenu(); }}
                      className="block w-full text-left py-2 px-4 text-muted-foreground hover:text-foreground transition-colors border-b border-border"
                    >
                      Login
                    </button>
                    <button 
                      onClick={() => { handleNavigation('/signup'); toggleMobileMenu(); }}
                      className="block w-full text-left py-2 px-4 bg-primary text-primary-foreground rounded-md text-center transition-colors"
                    >
                      Sign Up
                    </button>
                  </div>
                </div>
                {/* Close Button for Mobile Menu */}
                <div className="p-4 border-t border-border">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    className="w-full justify-center"
                    onClick={toggleMobileMenu}
                  >
                    Close Menu
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        {children}
      </main>

      {/* Footer: Updated with brand information and cleaned up links */}
      <footer className="bg-muted/20 border-t border-border py-8 mt-auto">
        <div className="container max-w-7xl mx-auto px-4 text-center">
          <div className="flex justify-center items-center space-x-6 mb-4">
            <button onClick={() => handleNavigation('/privacy')} className="text-sm text-muted-foreground hover:text-foreground transition-colors">Privacy</button>
            <button onClick={() => handleNavigation('/terms')} className="text-sm text-muted-foreground hover:text-foreground transition-colors">Terms</button>
            <button onClick={() => handleNavigation('/about')} className="text-sm text-muted-foreground hover:text-foreground transition-colors">About</button>
          </div>
          <p className="text-sm text-muted-foreground">
            © 2025 WightWay. All rights reserved.
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            WightWay and WightWay Connect are brands of LPIWuk LTD (Company No. 12196636)
          </p>
        </div>
      </footer>
    </div>
  );
};